import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, ClipboardList, PowerOff } from "lucide-react";
import { plansApi } from "@/lib/planApi";
import PlanTree from "@/features/plans/components/PlanTree";
import ISPUploader from "@/features/plans/components/ISPUploader";

const STATUS_STYLES = {
    draft: "bg-amber-100 text-amber-800 border-amber-200",
    active: "bg-brand-green-100 text-brand-green-800 border-brand-green-200",
    inactive: "bg-slate-200 text-slate-700 border-slate-300",
};

const StatusPill = ({ status }) => (
    <span
        data-testid={`plan-status-${status}`}
        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider ${
            STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200"
        }`}
    >
        {status}
    </span>
);

const PlanBuilderPage = () => {
    const { id } = useParams();
    const [plan, setPlan] = useState(null);
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deactivating, setDeactivating] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const t = await plansApi.tree(id);
            setPlan(t);
            setTree(t.outcomes || []);
        } catch (err) {
            const msg = err?.message || "Could not load plan";
            toast.error(msg);
            setPlan(null);
            setTree(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleDeactivate = async () => {
        if (
            !window.confirm(
                "Deactivating this plan will free the individual to activate another one. Historical data is preserved. Continue?",
            )
        ) {
            return;
        }
        setDeactivating(true);
        try {
            await plansApi.deactivate(id);
            toast.success("Plan deactivated");
            await load();
        } catch (err) {
            toast.error(err?.message || "Deactivate failed");
        } finally {
            setDeactivating(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-16 text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading plan…
            </div>
        );
    }
    if (!plan) return null;

    return (
        <div className="mx-auto max-w-6xl" data-testid="plan-builder-page">
            <Link
                to={`/individuals/${plan.individual_id}`}
                className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-blue-700"
            >
                <ArrowLeft className="h-4 w-4" /> Back to individual
            </Link>

            <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue-700">
                        Plan Builder
                    </p>
                    <h1 className="mt-1 font-heading text-3xl font-bold text-brand-blue-950">
                        {plan.title}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <StatusPill status={plan.status} />
                        <span>· Outcomes: {(tree || []).length}</span>
                    </div>
                </div>
                {plan.status === "active" && (
                    <button
                        type="button"
                        onClick={handleDeactivate}
                        disabled={deactivating}
                        data-testid="plan-deactivate-button"
                        className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-60"
                    >
                        {deactivating ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <PowerOff className="h-4 w-4" />
                        )}
                        Deactivate plan
                    </button>
                )}
            </header>

            <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 font-heading text-lg font-bold text-brand-blue-950">
                    ISP PDF ingestion
                </h2>
                <ISPUploader plan={plan} onApplied={load} />
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <h2 className="mb-4 font-heading text-lg font-bold text-brand-blue-950">
                    <ClipboardList className="mr-2 inline h-5 w-5 text-brand-blue-700" />
                    Current plan structure
                </h2>
                <PlanTree outcomes={tree || []} />
            </section>
        </div>
    );
};

export default PlanBuilderPage;
